import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getLoginUrl, isOnLoginPage } from './utils';
import {
  Ticket, 
  TicketWithRelations, 
  Department, 
  DepartmentMember,
  TicketType, 
  Stage, 
  Comment, 
  DashboardStats, 
  AuthUser, 
  AppRole, 
  TicketFilters,
  PublicTicketResponse,
  Workflow,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  Task,
  TaskCreateRequest,
  TaskUpdateRequest,
  TaskComment,
  LeadDashboardData,
  AgentRequest,
  RequestStatus,
} from './types';

// Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  details?: unknown;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstLogin?: boolean;
    profile?: {
      id: string;
      name: string;
      position?: string;
      avatarUrl?: string;
      isActive: boolean;
    };
    roles: string[];
  };
  accessToken: string;
  refreshToken: string;
}

export interface TicketCreateRequest {
  requesterName: string;
  requesterPhone?: string;
  requesterEmail: string;
  departmentId: string;
  account: string;
  ticketTypeId: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  relatedSystem?: 'crm' | 'erp' | 'website' | 'other';
  expectedOutcome?: string;
  attachments?: string[];
}

export interface TicketUpdateRequest {
  priority?: 'low' | 'medium' | 'high' | 'critical';
  description?: string;
  relatedSystem?: 'crm' | 'erp' | 'website' | 'other';
  expectedOutcome?: string;
  stageId?: string;
  dueDate?: string;
  estimatedValue?: number;
  estimatedType?: 'hours' | 'days' | 'weeks';
  resolvedAt?: string;
  closedAt?: string;
}

export interface CommentCreateRequest {
  ticketId: string;
  content: string;
  isInternal: boolean;
  isFromRequester?: boolean;
}

export interface PublicCommentCreateRequest {
  content: string;
  requesterEmail: string;
}

export interface PrepareUploadRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedByRequester?: boolean;
  ticketId?: string;
}

export interface PrepareUploadResponse {
  attachmentId: string;
  uploadUrl: string;
  headers: Record<string, string>;
  maxSize: number;
  fileUrl: string;
  bucket: string;
}

export interface VerifyUploadResponse {
  exists: boolean;
}

export interface HealthCheckResponse {
  success: boolean;
  status: string;
  timestamp: string;
  environment: string;
  database: string;
}

// API Client Class
class ApiClient {
  private client: AxiosInstance;
  private tokenKey = 'tickettrax_token';
  private refreshTokenKey = 'tickettrax_refresh_token';

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => {
        // Check if token refresh is needed
        if (response.headers['x-token-refresh-required'] === 'true') {
          this.refreshToken().catch(console.error);
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const isAuthEndpoint =
          originalRequest?.url?.includes('/auth/login') ||
          originalRequest?.url?.includes('/auth/refresh');

        // Enrich error with API message so toasts show backend message, not status code
        const apiMessage = error.response?.data?.message ?? error.response?.data?.error;
        if (apiMessage && typeof apiMessage === 'string') {
          error.message = apiMessage;
        }

        // Never try refresh for login or refresh: 401 = wrong credentials or refresh failed
        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();
            const token = this.getToken();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            const refreshMsg = (refreshError as { response?: { data?: { message?: string; error?: string } }; message?: string }).response?.data?.message
              ?? (refreshError as { response?: { data?: { error?: string } } }).response?.data?.error;
            if (refreshMsg) (refreshError as Error).message = refreshMsg;
            // Avoid redirect loop: if we're already on login page (e.g. in-flight 401 after navigate), don't redirect again
            if (isOnLoginPage()) {
              this.clearTokensOnly();
              return Promise.reject(refreshError);
            }
            // Clear tokens synchronously before redirect so next page load sees no token
            this.clearTokensOnly();
            window.location.href = getLoginUrl();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  private getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  private clearTokens(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  /** Normalize backend user to frontend */
  private normalizeAuthUser(raw: Record<string, unknown>): AuthResponse['user'] {
    const r = raw as {
      id: string;
      email: string;
      firstLogin?: boolean;
      profile?: { id?: string; name?: string; position?: string; avatarUrl?: string; isActive?: boolean };
      role?: string;
      roles?: string[];
    };
    const roles = Array.isArray(r.roles) ? r.roles : r.role ? [r.role] : [];
    return {
      id: r.id,
      email: r.email,
      firstLogin: r.firstLogin,
      profile: r.profile
        ? {
            id: r.profile.id ?? '',
            name: r.profile.name ?? '',
            position: r.profile.position,
            avatarUrl: r.profile.avatarUrl,
            isActive: r.profile.isActive ?? true,
          }
        : undefined,
      roles,
    };
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.client.post<
        ApiResponse<{ user: unknown; accessToken: string; refreshToken: string }>
      >('/auth/login', credentials);

      if (response.data.success && response.data.data) {
        const { user: rawUser, accessToken, refreshToken } = response.data.data;
        this.setTokens(accessToken, refreshToken);
        const user = this.normalizeAuthUser(rawUser as Record<string, unknown>);
        return { user, accessToken, refreshToken };
      }

      throw new Error('Invalid email or password');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      const apiMessage = ax.response?.data?.message ?? ax.response?.data?.error ?? '';
      const isDeactivated = apiMessage && /deactivated|inactive|account\s*(is\s*)?disabled/i.test(apiMessage);
      if (isDeactivated) {
        throw new Error(apiMessage);
      }
      throw new Error('Invalid email or password');
    }
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokens();
    }
  }

  /** Clear tokens only (sync). Use when redirecting immediately e.g. force_logout to avoid reload loop. */
  clearTokensOnly(): void {
    this.clearTokens();
  }

  async refreshToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.client.post<ApiResponse<{ accessToken: string }>>('/auth/refresh', {
      refreshToken,
    });

    if (response.data.success && response.data.data) {
      const { accessToken } = response.data.data;
      localStorage.setItem(this.tokenKey, accessToken);
      return accessToken;
    }

    throw new Error('Token refresh failed');
  }

  async getCurrentUser(): Promise<AuthResponse['user']> {
    const response = await this.client.get<ApiResponse<Record<string, unknown>>>('/auth/me');

    if (response.data.success && response.data.data) {
      return this.normalizeAuthUser(response.data.data);
    }

    throw new Error((response.data as { error?: string }).error || 'Failed to get current user');
  }

  async updateProfile(data: { name?: string; position?: string; avatarUrl?: string }): Promise<AuthResponse['user']> {
    const response = await this.client.put<ApiResponse<AuthResponse['user']>>('/auth/profile', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update profile');
  }

  async changePassword(email: string, password: string, confirmPassword: string): Promise<void> {
    const response = await this.client.put<ApiResponse>('/config/users/password', {
      email,
      password,
      confirmPassword,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Password update failed');
    }
  }

  /** Request password reset email (forgot password) */
  async forgetPassword(email: string): Promise<void> {
    const response = await this.client.post<ApiResponse>('/auth/forget-password', { email });
    if (!response.data.success) {
      throw new Error((response.data as { error?: string }).error || 'Failed to send reset email');
    }
  }

  /** Reset password using token from email link */
  async resetPassword(data: { token: string; newPassword: string; confirmPassword: string }): Promise<void> {
    const response = await this.client.post<ApiResponse>('/auth/reset-password', data);
    if (!response.data.success) {
      throw new Error((response.data as { error?: string }).error || 'Failed to reset password');
    }
  }

  // Ticket methods
  async createTicket(ticketData: TicketCreateRequest): Promise<Ticket> {
    const response = await this.client.post<ApiResponse<Ticket>>('/tickets', ticketData);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to create ticket');
  }

  async getTickets(params?: TicketFilters): Promise<PaginatedResponse<TicketWithRelations>> {
    const response = await this.client.get<PaginatedResponse<TicketWithRelations>>('/tickets', { params });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.error || 'Failed to fetch tickets');
  }

  async getTicketById(id: string): Promise<TicketWithRelations> {
    const response = await this.client.get<ApiResponse<TicketWithRelations>>(`/tickets/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch ticket');
  }

  async updateTicket(id: string, updateData: TicketUpdateRequest): Promise<Ticket> {
    const response = await this.client.put<ApiResponse<Ticket>>(`/tickets/${id}`, updateData);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update ticket');
  }

  async assignTicket(id: string, userIds: string[]): Promise<void> {
    const response = await this.client.post<ApiResponse>(`/tickets/${id}/assign`, { userIds });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to assign ticket');
    }
  }

  async unassignTicket(id: string, userIds: string[]): Promise<void> {
    const response = await this.client.post<ApiResponse>(`/tickets/${id}/unassign`, { userIds });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to unassign ticket');
    }
  }

  async updateTicketStage(id: string, stageId: string): Promise<Ticket> {
    const response = await this.client.put<ApiResponse<Ticket>>(`/tickets/${id}/stage`, { stageId });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update ticket stage');
  }

  async deleteTicket(id: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/tickets/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete ticket');
    }
  }

  // Public ticket methods
  async getTicketByToken(token: string): Promise<PublicTicketResponse> {
    const response = await this.client.get<ApiResponse<PublicTicketResponse>>(`/tickets/track/${token}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch ticket');
  }

  async addPublicComment(token: string, commentData: PublicCommentCreateRequest): Promise<void> {
    const response = await this.client.post<ApiResponse>(`/tickets/track/${token}/comments`, commentData);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add comment');
    }
  }

  // Comment methods
  async getComments(ticketId: string): Promise<Comment[]> {
    const response = await this.client.get<ApiResponse<Comment[]>>(`/tickets/${ticketId}/comments`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async createComment(ticketId: string, commentData: { content: string; isInternal: boolean }): Promise<Comment> {
    const response = await this.client.post<ApiResponse<Comment>>(`/tickets/${ticketId}/comments`, commentData);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to create comment');
  }

  // Configuration methods
  async getAllConfig(): Promise<{
    departments: Department[];
    ticketTypes: TicketType[];
    stages: Stage[];
  }> {
    const response = await this.client.get<ApiResponse<{
      departments: Department[];
      ticketTypes: TicketType[];
      stages: Stage[];
    }>>('/config/all');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch configuration');
  }

  async getDepartments(includeInactive = false): Promise<Department[]> {
    const params = includeInactive ? { includeInactive: 'true' } : {};
    const response = await this.client.get<ApiResponse<Department[]>>('/config/departments', { params });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch departments');
  }

  async getDepartmentById(id: string): Promise<Department> {
    const response = await this.client.get<ApiResponse<Department>>(`/config/departments/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch department');
  }

  async getAvailableTeamLeaders(): Promise<DepartmentMember[]> {
    const response = await this.client.get<ApiResponse<DepartmentMember[]>>('/config/departments/available-team-leaders');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch available team leaders');
  }

  async getAvailableAgents(): Promise<DepartmentMember[]> {
    const response = await this.client.get<ApiResponse<DepartmentMember[]>>('/config/departments/available-agents');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch available agents');
  }

  async getTicketTypes(): Promise<TicketType[]> {
    const response = await this.client.get<ApiResponse<TicketType[]>>('/config/ticket-types');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch ticket types');
  }

  async getStages(): Promise<Stage[]> {
    const response = await this.client.get<ApiResponse<Stage[]>>('/config/stages');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch stages');
  }

  // Statistics
  async getTicketStats(): Promise<DashboardStats> {
    const response = await this.client.get<ApiResponse<DashboardStats>>('/tickets/stats');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch ticket statistics');
  }

  // Health check
  async healthCheck(): Promise<HealthCheckResponse> {
    const response = await this.client.get<HealthCheckResponse>('/health');
    return response.data;
  }

  // Check authentication status
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // User management methods
  async getUsers(): Promise<AuthUser[]> {
    const response = await this.client.get<ApiResponse<AuthUser[]>>('/config/users');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch users');
  }

  async createUser(userData: {
    email: string;
    name: string;
    position?: string;
    role: AppRole;
  }): Promise<AuthUser> {
    const response = await this.client.post<ApiResponse<AuthUser>>('/config/users', userData);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to create user');
  }

  async updateUser(userId: string, updates: Partial<AuthUser> & { roles?: AppRole[]; role?: AppRole }): Promise<AuthUser> {
    const body = { ...updates } as Record<string, unknown>;
    if ('roles' in body && body.roles !== undefined) {
      const role = Array.isArray(body.roles) ? body.roles[0] : body.roles;
      body.role = role;
      delete body.roles;
    }
    const response = await this.client.put<ApiResponse<AuthUser>>(`/config/users/${userId}`, body);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update user');
  }

  /** Returns success or requiresReassignment + tickets when user has assigned tickets */
  async deleteUser(id: string): Promise<
    | { success: true }
    | { success: false; requiresReassignment: true; tickets: Array<{ id: string; ticketNumber: string; description: string }> }
  > {
    const response = await this.client.delete<
      ApiResponse & {
        requiresReassignment?: boolean;
        tickets?: Array<{ id: string; ticketNumber: string; description?: string }>;
      }
    >(`/config/users/${id}`);

    const body = response.data as Record<string, unknown>;
    const data = (body.data as Record<string, unknown> | undefined) ?? body;
    const requiresReassignment = Boolean(body.requiresReassignment ?? data.requiresReassignment);
    const ticketsRaw = (body.tickets ?? data.tickets) as Array<{ id: string; ticketNumber: string; description?: string }> | undefined;
    const tickets = Array.isArray(ticketsRaw)
      ? ticketsRaw.map((t) => ({ id: t.id, ticketNumber: t.ticketNumber, description: t.description ?? '' }))
      : [];

    if (body.success === false && requiresReassignment && tickets.length > 0) {
      return { success: false, requiresReassignment: true, tickets };
    }
    if (body.success === false && requiresReassignment) {
      return { success: false, requiresReassignment: true, tickets };
    }
    if (response.data.success === true || body.success === true) {
      return { success: true };
    }
    throw new Error((body.error as string) || (body.message as string) || 'Failed to delete user');
  }

  async reassignUserTickets(body: {
    fromUserId: string;
    toUserId: string;
    ticketsIds: string[];
  }): Promise<void> {
    const response = await this.client.post<ApiResponse & { data?: { reassigned?: number } }>('/tickets/reassign', body);
    if (!response.data.success) {
      throw new Error((response.data as { error?: string }).error || 'Failed to reassign tickets and delete user');
    }
  }

  async toggleUserStatus(userId: string): Promise<AuthUser> {
    const response = await this.client.put<ApiResponse<AuthUser>>(`/config/users/${userId}/status`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to toggle user status');
  }

  // Department management methods (per DEPARTMENT_FLOW.md). Backend expects teamLeadersIds (with 's').
  async createDepartment(data: { name: string; teamLeaderId?: string; teamLeaderIds?: string[]; agentsIds?: string[] }): Promise<Department> {
    const body: Record<string, unknown> = { name: data.name };
    if (data.teamLeaderIds?.length) body.teamLeadersIds = data.teamLeaderIds;
    if (data.agentsIds?.length) body.agentsIds = data.agentsIds;
    const response = await this.client.post<ApiResponse<Department>>('/config/departments', body);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to create department');
  }

  async updateDepartment(id: string, data: { name?: string; isActive?: boolean; teamLeaderId?: string | null; teamLeaderIds?: string[]; agentsIds?: string[] }): Promise<Department> {
    const body: Record<string, unknown> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.isActive !== undefined) body.isActive = data.isActive;
    if (data.teamLeaderIds !== undefined) body.teamLeadersIds = data.teamLeaderIds;
    if (data.agentsIds !== undefined) body.agentsIds = data.agentsIds;
    const response = await this.client.put<ApiResponse<Department>>(`/config/departments/${id}`, body);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update department');
  }

  async deleteDepartment(id: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/config/departments/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete department');
    }
  }

  // Ticket type management methods
  async createTicketType(data: { name: string }): Promise<TicketType> {
    const response = await this.client.post<ApiResponse<TicketType>>('/config/ticket-types', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to create ticket type');
  }

  async updateTicketType(id: string, data: { name: string }): Promise<TicketType> {
    const response = await this.client.put<ApiResponse<TicketType>>(`/config/ticket-types/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update ticket type');
  }

  async deleteTicketType(id: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/config/ticket-types/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete ticket type');
    }
  }

  // Stage management methods
  async createStage(data: { name: string; color: string; position: number; type?: 'default' | 'resolved' | 'closed'; assigneeIds?: string[] }): Promise<Stage> {
    const response = await this.client.post<ApiResponse<Stage>>('/config/stages', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to create stage');
  }

  async updateStage(id: string, data: { name: string; color: string; type?: 'default' | 'resolved' | 'closed'; assigneeIds?: string[] }): Promise<Stage> {
    const response = await this.client.put<ApiResponse<Stage>>(`/config/stages/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to update stage');
  }

  /** Returns success or requiresReassignment + tickets when stage has tickets */
  async deleteStage(id: string): Promise<
    | { success: true }
    | { success: false; requiresReassignment: true; tickets: Array<{ id: string; ticketNumber: string; description: string }> }
  > {
    const response = await this.client.delete<
      ApiResponse & {
        requiresReassignment?: boolean;
        tickets?: Array<{ id: string; ticketNumber: string; description?: string }>;
      }
    >(`/config/stages/${id}`);

    const body = response.data as Record<string, unknown>;
    const data = (body.data as Record<string, unknown> | undefined) ?? body;
    const requiresReassignment = Boolean(body.requiresReassignment ?? data.requiresReassignment);
    const ticketsRaw = (body.tickets ?? data.tickets) as Array<{ id: string; ticketNumber: string; description?: string }> | undefined;
    const tickets = Array.isArray(ticketsRaw)
      ? ticketsRaw.map((t) => ({ id: t.id, ticketNumber: t.ticketNumber, description: t.description ?? '' }))
      : [];

    if (body.success === false && requiresReassignment) {
      return { success: false, requiresReassignment: true, tickets };
    }
    if (response.data.success === true || body.success === true) {
      return { success: true };
    }
    throw new Error((body.error as string) || (body.message as string) || 'Failed to delete stage');
  }

  async reassignStageTickets(body: {
    fromStageId: string;
    toStageId: string;
    ticketsIds: string[];
  }): Promise<void> {
    const response = await this.client.post<ApiResponse>('/config/stages/reassign', body);
    if (!response.data.success) {
      throw new Error((response.data as { error?: string }).error || 'Failed to reassign tickets and delete stage');
    }
  }

  async reorderStages(stageIds: string[]): Promise<void> {
    const response = await this.client.put<ApiResponse>('/config/stages/reorder', { stageIds });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to reorder stages');
    }
  }

  // Workflow methods
  async getWorkflows(): Promise<Workflow[]> {
    const response = await this.client.get<ApiResponse<Workflow[]>>('/workflows');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch workflows');
  }

  async getWorkflowById(id: string): Promise<Workflow> {
    const response = await this.client.get<ApiResponse<Workflow>>(`/workflows/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch workflow');
  }

  async getWorkflowByUser(userId: string): Promise<Workflow> {
    const response = await this.client.get<ApiResponse<Workflow>>(`/workflows/user/${userId}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch user workflow');
  }

  /** Workflows assigned to a department (for team leaders: use this to know if department can create tasks) */
  async getWorkflowsByDepartmentId(departmentId: string): Promise<Workflow[]> {
    const response = await this.client.get<ApiResponse<Workflow[]>>(`/workflows/department/${departmentId}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch workflows for department');
  }

  async getUsersWithoutWorkflow(): Promise<Array<{ id: string; email: string }>> {
    const response = await this.client.get<ApiResponse<Array<{ id: string; email: string }>>>('/workflows/users/no-workflow');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch users without workflow');
  }

  async getDepartmentsWithoutWorkflow(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await this.client.get<ApiResponse<Array<{ id: string; name: string }>>>('/workflows/departments/no-workflow');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
    } catch (_) {
      // Backend may not have this route yet; return empty list so UI still works
    }
    return [];
  }

  async createWorkflow(data: WorkflowCreateRequest): Promise<Workflow> {
    const body: Record<string, unknown> = { name: data.name };
    if (data.stages?.length) body.stages = data.stages;
    if (data.userIds?.length) body.userIds = data.userIds;
    else if (data.departmentsIds?.length) body.departmentsIds = data.departmentsIds;
    const response = await this.client.post<ApiResponse<Workflow>>('/workflows', body);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to create workflow');
  }

  async updateWorkflow(id: string, data: WorkflowUpdateRequest): Promise<Workflow> {
    const body: Record<string, unknown> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.stages !== undefined) body.stages = data.stages;
    if (data.userIds !== undefined) body.userIds = data.userIds;
    else if (data.departmentsIds !== undefined) body.departmentsIds = data.departmentsIds;
    const response = await this.client.put<ApiResponse<Workflow>>(`/workflows/${id}`, body);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to update workflow');
  }

  async deleteWorkflow(id: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/workflows/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete workflow');
    }
  }

  // Task methods
  async getTasks(): Promise<Task[]> {
    const response = await this.client.get<ApiResponse<Task[]>>('/tasks');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch tasks');
  }

  /** Team Leader only — GET /api/tasks/lead-dashboard */
  async getLeadDashboard(): Promise<LeadDashboardData> {
    const response = await this.client.get<ApiResponse<LeadDashboardData>>('/tasks/lead-dashboard');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch lead dashboard');
  }

  async getTaskById(id: string): Promise<Task> {
    const response = await this.client.get<ApiResponse<Task>>(`/tasks/${id}`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch task');
  }

  async getTasksByAgent(agentId: string): Promise<Task[]> {
    const response = await this.client.get<ApiResponse<Task[]>>(`/tasks/agent/${agentId}`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch tasks');
  }

  async createTask(data: TaskCreateRequest): Promise<Task> {
    const response = await this.client.post<ApiResponse<Task>>('/tasks', data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to create task');
  }

  /** Team Leader — PATCH /api/tasks/:id */
  async updateTask(id: string, data: TaskUpdateRequest): Promise<Task> {
    const response = await this.client.patch<ApiResponse<Task>>(`/tasks/${id}`, data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to update task');
  }

  async updateTaskStatus(id: string, stageId: string): Promise<Task> {
    const response = await this.client.patch<ApiResponse<Task>>(`/tasks/${id}/stage`, { stageId });
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to update task status');
  }

  async deleteTask(id: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/tasks/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete task');
  }

  // Task comment methods
  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    const response = await this.client.get<ApiResponse<TaskComment[]>>(`/tasks/${taskId}/comments`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch task comments');
  }

  async addTaskComment(
    taskId: string,
    data: { content: string; attachmentsIds?: string[] },
  ): Promise<TaskComment> {
    const response = await this.client.post<ApiResponse<TaskComment>>(`/tasks/${taskId}/comments`, data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to add comment');
  }

  async updateTaskComment(
    commentId: string,
    data: { content: string; attachmentsIds?: string[] },
  ): Promise<TaskComment | null> {
    const response = await this.client.patch<ApiResponse<TaskComment>>(
      `/tasks/comments/${commentId}`,
      data,
    );
    if (response.data.success) return response.data.data ?? null;
    throw new Error(response.data.error || 'Failed to update comment');
  }

  async deleteTaskComment(commentId: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/tasks/comments/${commentId}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete comment');
  }

  // Request methods — (backend implements /api/requests)
  async getRequests(status?: RequestStatus): Promise<AgentRequest[]> {
    const apiStatus = status === 'rejected' ? 'declined' : status;
    const params = apiStatus ? { status: apiStatus } : {};
    const response = await this.client.get<ApiResponse<AgentRequest[]>>('/requests', { params });
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch requests');
  }

  async getRequestById(id: string): Promise<AgentRequest> {
    const response = await this.client.get<ApiResponse<AgentRequest>>(`/requests/${id}`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to fetch request');
  }

  async createRequest(message: string): Promise<AgentRequest> {
    const response = await this.client.post<ApiResponse<AgentRequest>>('/requests', { message });
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to create request');
  }

  async updateRequest(id: string, message: string): Promise<AgentRequest> {
    const response = await this.client.patch<ApiResponse<AgentRequest>>(`/requests/${id}`, { message });
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to update request');
  }

  async updateRequestStatus(id: string, status: 'approved' | 'rejected'): Promise<AgentRequest> {
    const bodyStatus = status === 'rejected' ? 'declined' : status;
    const response = await this.client.patch<ApiResponse<AgentRequest>>(`/requests/${id}/status`, {
      status: bodyStatus,
    });
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Failed to update request status');
  }

  // Storage methods
  async prepareUpload(data: PrepareUploadRequest): Promise<PrepareUploadResponse> {
    const response = await this.client.post<ApiResponse<PrepareUploadResponse>>('/storage/prepare-upload', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to prepare upload');
  }

  async verifyUpload(attachmentId: string): Promise<boolean> {
    const response = await this.client.get<ApiResponse<boolean>>(`/storage/verify-upload/${attachmentId}`);

    if (response.data.success) {
      return response.data.data === true;
    }

    return false;
  }

  async getDownloadUrl(attachmentId: string): Promise<string> {
    const response = await this.client.get<ApiResponse<string>>(`/storage/get-download-url/${attachmentId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to get download URL');
  }

  async deleteFile(attachmentId: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/storage/delete-file/${attachmentId}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete file');
    }
  }
}

// Create and export API client instance
export const apiClient = new ApiClient();
